import { useState } from 'react';

import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  useIsMobile,
} from '@design-system';
import {
  IntakeFormQuickPlanForm,
  QuickPlanForm,
  QuickPlanModeSelector,
  useShortFormIntake,
  type QuickPlanFormValues,
  type QuickPlanMode,
} from '@/features/client-quick-plan';

import { ArrowLeft } from 'lucide-react';

interface QuickPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  advisorId: string;
  onSubmitSuccess?: (data: ShortFormIntakeResponse) => void;
}

function getDialogCopy(mode: QuickPlanMode | null) {
  switch (mode) {
    case 'quick-plan':
      return {
        title: 'New Case Quick Start',
        description: 'Enter basic client info to create a plan instantly. No email invite needed.',
      };
    case 'intake-form':
      return {
        title: 'Send Medical Intake Form',
        description: 'Enter basic info and email to generate a plan and send the intake form.',
      };
    default:
      return {
        title: 'New Case Quick Start',
        description: "Choose how you'd like to create this plan.",
      };
  }
}

export function QuickPlanDialog({
  open,
  onOpenChange,
  advisorId,
  onSubmitSuccess,
}: QuickPlanDialogProps) {
  const isMobile = useIsMobile();
  const [mode, setMode] = useState<QuickPlanMode | null>(null);
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const { submitQuickPlan, isPending } = useShortFormIntake({
    advisorId,
    onSuccess: onSubmitSuccess,
  });

  function handleSubmit(data: QuickPlanFormValues) {
    submitQuickPlan(data);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setMode(null);
    }
    onOpenChange(nextOpen);
  }

  function handleDone() {
    setMode(null);
    onOpenChange(false);
    onSubmitSuccess?.({} as ShortFormIntakeResponse);
  }

  const { title, description } = getDialogCopy(mode);

  const content = (
    <>
      {mode !== null && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn('w-fit', {
            'mb-6': isMobile,
          })}
          onClick={() => setMode(null)}
        >
          <ArrowLeft />
          Back
        </Button>
      )}

      {mode === null && <QuickPlanModeSelector onSelectMode={setMode} />}

      {mode === 'quick-plan' && (
        <QuickPlanForm
          onSubmit={handleSubmit}
          isSubmitting={isPending}
          variant="dialog"
          onPartnerDialogChange={setShowPartnerDialog}
        />
      )}

      {mode === 'intake-form' && (
        <IntakeFormQuickPlanForm
          advisorId={advisorId}
          onDone={handleDone}
          onPartnerDialogChange={setShowPartnerDialog}
        />
      )}
    </>
  );

  if (!isMobile) {
    return (
      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
      >
        <DialogContent
          className={cn('max-h-[90vh] max-w-lg overflow-y-auto transition-all duration-200', {
            'max-h-128 min-h-128 -translate-y-[calc(50%+1rem)] scale-[0.94]': showPartnerDialog,
          })}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DrawerContent
        className={cn('transition-all! duration-200! max-h-[90vh] overflow-y-auto px-4 pb-6', {
          'h-144 bottom-1! scale-[0.96]': showPartnerDialog,
        })}
      >
        <DrawerHeader className="text-left">
          <DrawerTitle>{title}</DrawerTitle>
          <DrawerDescription>{description}</DrawerDescription>
        </DrawerHeader>
        {content}
      </DrawerContent>
    </Drawer>
  );
}
